import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Lock, RefreshCw, TrendingUp, Users, Car, Bike, Truck, IndianRupee, BarChart3, Percent } from "lucide-react";
import { supabase } from "@/supabaseClient";

const ACCESS_PIN = 'clearwayy2026';

const PRICING = { car: 333, bike: 279, auto: 225 };

const formatINR = (num) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(num);

// ─── PIN Gate ───
const PinGate = ({ onUnlock }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (pin === ACCESS_PIN) {
      onUnlock();
    } else {
      setError(true);
      setTimeout(() => setError(false), 1500);
      setPin('');
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-sm"
      >
        <div className="bg-[#111] rounded-3xl p-8 border border-white/5 shadow-2xl text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-emerald-500/10 flex items-center justify-center">
            <Lock className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-1">Partner Dashboard</h1>
          <p className="text-white/40 text-sm mb-6">Enter access PIN to continue</p>
          <form onSubmit={handleSubmit}>
            <input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Access PIN"
              data-testid="pin-input"
              className={`w-full h-12 rounded-xl bg-white/5 border text-white text-center text-lg tracking-widest placeholder:text-white/20 focus:outline-none transition-colors ${
                error ? 'border-red-500 shake' : 'border-white/10 focus:border-emerald-500/50'
              }`}
            />
            <button
              type="submit"
              data-testid="pin-submit"
              className="w-full mt-4 h-12 rounded-xl bg-emerald-500 text-white font-bold text-sm hover:bg-emerald-600 transition-colors"
            >
              Unlock Dashboard
            </button>
          </form>
          {error && <p className="text-red-400 text-xs mt-3">Invalid PIN. Try again.</p>}
        </div>
      </motion.div>
      <style>{`
        .shake { animation: shake 0.3s ease-in-out; }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-6px); }
          75% { transform: translateX(6px); }
        }
      `}</style>
    </div>
  );
};

// ─── Metric Card ───
const MetricCard = ({ icon: Icon, label, value, sub, color = "emerald" }) => {
  const colors = {
    emerald: { bg: "bg-emerald-500/10", text: "text-emerald-400" },
    orange: { bg: "bg-orange-500/10", text: "text-orange-400" },
    blue: { bg: "bg-blue-500/10", text: "text-blue-400" },
    purple: { bg: "bg-purple-500/10", text: "text-purple-400" },
    white: { bg: "bg-white/5", text: "text-white/70" },
  };
  const c = colors[color] || colors.emerald;

  return (
    <div className="bg-[#111] rounded-2xl p-6 border border-white/5">
      <div className={`w-10 h-10 rounded-xl ${c.bg} flex items-center justify-center mb-4`}>
        <Icon className={`w-5 h-5 ${c.text}`} />
      </div>
      <p className="text-white/40 text-xs font-medium uppercase tracking-wider mb-1">{label}</p>
      <p className="text-2xl font-black text-white">{value}</p>
      {sub && <p className="text-white/30 text-xs mt-1">{sub}</p>}
    </div>
  );
};

// ─── Dashboard Content ───
const DashboardContent = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_partner_stats');
      if (error) throw error;

      // RPC returns { total_stickers, registered, by_vehicle_type: { bike: N, car: N } }
      // data may be the object directly, wrapped in array, or stringified
      let stats = data;
      if (Array.isArray(stats)) stats = stats[0];
      if (typeof stats === 'string') stats = JSON.parse(stats);
      if (!stats) throw new Error('Empty response');

      const totalProvisioned = stats.total_stickers || 0;
      const totalActivated = stats.registered || 0;

      // by_vehicle_type may omit keys with 0 count — default all to 0, case-insensitive
      const raw = stats.by_vehicle_type || {};
      const lower = {};
      for (const [k, v] of Object.entries(raw)) {
        lower[k.toLowerCase()] = v;
      }
      const segments = {
        car: lower.car ?? 0,
        bike: lower.bike ?? 0,
        auto: lower.auto ?? 0,
      };

      const grossRevenue =
        segments.car * PRICING.car +
        segments.bike * PRICING.bike +
        segments.auto * PRICING.auto;

      const activationRate = totalProvisioned > 0
        ? ((totalActivated / totalProvisioned) * 100).toFixed(1)
        : '0.0';

      setMetrics({
        totalProvisioned,
        totalActivated,
        segments,
        grossRevenue,
        activationRate,
      });
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  if (loading && !metrics) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  const m = metrics || { totalProvisioned: 0, totalActivated: 0, segments: { car: 0, bike: 0, auto: 0 }, grossRevenue: 0, activationRate: '0.0' };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="partner-dashboard">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-white">Clearwayy <span className="text-emerald-400">Partner Dashboard</span></h1>
          </div>
          <div className="flex items-center gap-4">
            {lastRefresh && (
              <span className="text-white/30 text-xs hidden sm:inline">
                Updated {lastRefresh.toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={fetchMetrics}
              disabled={loading}
              data-testid="refresh-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500/10 text-emerald-400 text-sm font-medium hover:bg-emerald-500/20 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* Revenue Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 rounded-3xl p-8 border border-emerald-500/10"
        >
          <p className="text-emerald-400 text-xs font-bold uppercase tracking-widest mb-2">Total Gross Revenue</p>
          <p className="text-4xl sm:text-5xl font-black text-white tracking-tight" data-testid="gross-revenue">
            {formatINR(m.grossRevenue)}
          </p>
          <div className="flex flex-wrap gap-6 mt-4 text-sm text-white/40">
            <span>Cars: {m.segments.car} x ₹{PRICING.car}</span>
            <span>Bikes: {m.segments.bike} x ₹{PRICING.bike}</span>
            <span>Autos: {m.segments.auto} x ₹{PRICING.auto}</span>
          </div>
        </motion.div>

        {/* Primary Metrics */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard icon={BarChart3} label="Total Provisioned" value={m.totalProvisioned.toLocaleString()} sub="Baseline inventory" color="white" />
          <MetricCard icon={Users} label="Total Activated" value={m.totalActivated.toLocaleString()} sub="Registrations complete" color="emerald" />
          <MetricCard icon={Percent} label="Activation Rate" value={`${m.activationRate}%`} sub={`${m.totalActivated} of ${m.totalProvisioned}`} color="blue" />
          <MetricCard icon={IndianRupee} label="Avg. Revenue / Unit" value={m.totalActivated > 0 ? formatINR(Math.round(m.grossRevenue / m.totalActivated)) : '₹0'} sub="Per activated sticker" color="orange" />
        </div>

        {/* Segmented Vehicle Counts */}
        <div>
          <p className="text-white/40 text-xs font-bold uppercase tracking-widest mb-4">Vehicle Segmentation</p>
          <div className="grid sm:grid-cols-3 gap-4">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="bg-[#111] rounded-2xl p-6 border border-white/5 text-center">
              <Car className="w-8 h-8 text-blue-400 mx-auto mb-3" />
              <p className="text-3xl font-black text-white">{m.segments.car}</p>
              <p className="text-white/40 text-xs mt-1">Cars Activated</p>
              <p className="text-white/20 text-xs mt-0.5">{formatINR(m.segments.car * PRICING.car)} revenue</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="bg-[#111] rounded-2xl p-6 border border-white/5 text-center">
              <Bike className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
              <p className="text-3xl font-black text-white">{m.segments.bike}</p>
              <p className="text-white/40 text-xs mt-1">Bikes Activated</p>
              <p className="text-white/20 text-xs mt-0.5">{formatINR(m.segments.bike * PRICING.bike)} revenue</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
              className="bg-[#111] rounded-2xl p-6 border border-white/5 text-center">
              <Truck className="w-8 h-8 text-orange-400 mx-auto mb-3" />
              <p className="text-3xl font-black text-white">{m.segments.auto}</p>
              <p className="text-white/40 text-xs mt-1">Autos Activated</p>
              <p className="text-white/20 text-xs mt-0.5">{formatINR(m.segments.auto * PRICING.auto)} revenue</p>
            </motion.div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center pt-8 pb-4">
          <p className="text-white/20 text-xs">Clearwayy Partner Dashboard &middot; Data sourced from Supabase Realtime</p>
        </div>
      </div>
    </div>
  );
};

// ─── Main Export ───
const PartnerDashboard = () => {
  const [unlocked, setUnlocked] = useState(false);
  return unlocked ? <DashboardContent /> : <PinGate onUnlock={() => setUnlocked(true)} />;
};

export default PartnerDashboard;
